import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Badge } from "@/components/Badge/Badge";
import { Column } from "@/components/Datatable/types";
import {
  CourseListItem,
  CourseStatus,
} from "@/modules/learningCenter/features/CourseManagement/types/course";

interface CreateCourseColumnsProps {
  onEditCourse: (uuid: string) => void;
  onPublishCourse: (course: CourseListItem) => void;
  onArchiveCourse: (course: CourseListItem) => void;
}

const statusVariant = (
  status: CourseStatus,
): "default" | "grey" | "destructive" => {
  switch (status) {
    case "published":
      return "default";
    case "archived":
      return "destructive";
    case "draft":
    default:
      return "grey";
  }
};

const levelVariant = (): "grey" => "grey";

function CourseRowActions({
  course,
  onEditCourse,
  onPublishCourse,
  onArchiveCourse,
}: {
  course: CourseListItem;
} & CreateCourseColumnsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
          variant="ghost"
        >
          <span className="sr-only">Open menu</span>
          <EllipsisVerticalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[160px] p-2 bg-white dark:bg-slate-800"
      >
        <DropdownMenuItem
          className="flex items-center px-3 py-2 text-sm cursor-pointer text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          onClick={() => onEditCourse(course.uuid)}
        >
          Edit
        </DropdownMenuItem>
        {course.status !== "published" && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            onClick={() => onPublishCourse(course)}
          >
            Publish
          </DropdownMenuItem>
        )}
        {course.status !== "archived" && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            onClick={() => onArchiveCourse(course)}
          >
            Archive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function createCourseColumns({
  onEditCourse,
  onPublishCourse,
  onArchiveCourse,
}: CreateCourseColumnsProps): Column<CourseListItem>[] {
  return [
    {
      header: "Title",
      accessorKey: "title",
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-gray-900 dark:text-white">
            {row.original.title}
          </span>
          {row.original.tagline && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {row.original.tagline}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Difficulty",
      accessorKey: "level",
      cell: ({ row }) =>
        row.original.level ? (
          <Badge variant={levelVariant()}>{row.original.level}</Badge>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      header: "Status",
      accessorKey: "status",
      enableSorting: true,
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: "Duration",
      accessorKey: "estimatedHours",
      cell: ({ row }) =>
        row.original.estimatedHours ? (
          <span className="text-gray-900 dark:text-white">
            {row.original.estimatedHours}h
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      header: "Modules",
      accessorKey: "moduleCount",
      cell: ({ row }) => (
        <span className="text-gray-900 dark:text-white">
          {row.original.moduleCount}
        </span>
      ),
    },
    {
      header: "Enrolled",
      accessorKey: "totalEnrolled",
      cell: ({ row }) => (
        <span className="text-gray-900 dark:text-white">
          {row.original.totalEnrolled}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <CourseRowActions
          course={row.original}
          onArchiveCourse={onArchiveCourse}
          onEditCourse={onEditCourse}
          onPublishCourse={onPublishCourse}
        />
      ),
    },
  ];
}
